import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
    validateDetailedBookingSOVFields
} from '../../QueueSheetManagement/utils/validateBookingFieldsHelpers/validateDetailedBookingSOVFields';
import { GP_SLOT_VALUES } from '../constants/DetailedBookingConstants';
import { computeExistingNonContextualPercentage, computeExistingContextualNodePercentages } from '../services/bookingApprovalUtils';
import { FORM_FIELDS } from '../constants/FormFieldsConstants';
import {
    CreateDetailedBookingSOVFormProps, DetailedBookingSOVData, DetailedBookingSOVPartialDataErrors,
    PreviousSOVValues,
    SlotOptionType, SlotPackage, SOVContextualNodeData, SOVContextualNodeErrors
} from '../interface/DetailedBookingInterface';
import { handleInputChange } from '../services/sovFormHelpers/handleInputChange';
import { getEffectiveContextualPercentageAvailable, getEffectivePercentageAvailable } from '../services/sovFormHelpers/helpers';
import { processSlotData } from '../services/sovFormHelpers/loadSlotsData';
import { NON_CONTEXTUAL_PACKAGE_NAME } from '../../../constants/formConstants';
import { useGetArtEvents } from '../../../../EditArtEvent/hooks/useGetArtEvents';
import { useAppContext } from '../../../../Shared/store/appContext';

export const useCreateDetailedBookingSOVForm = ({ initialData,
    bookingId,
    entryId,
    onFormSubmit,
    validateFormTrigger,
    existingContextualNodes,
    resetDetailedFormErrors,
    setResetDetailedFormErrors,
    filteredSlotPackages,
    isEditMode,
    hideCommonFields,
    onSlotSelectionChange,
    preSelectedSlotNames,
    budgetUsedByOtherSlots = 0,
    onSlotBudgetChange,
}: CreateDetailedBookingSOVFormProps) => {
    const { t } = useTranslation();

    // Resolve whether the selected Art Event is actually IN waitlist mode — i.e. it has opted in
    // (waitlistEnabled === true), which is exactly what the backend checks to decide whether an
    // over-request is waitlisted vs rejected. This is distinct from waitlistFeatureEnabled (the Weblab
    // flag that only governs whether an event *may* opt in): a feature-available but not-opted-in event
    // must NOT show the waitlist over-request affordances. useGetArtEvents shares its React Query cache.
    const { state } = useAppContext();
    const { data: artEventsData, isLoading: isArtEventsLoading } = useGetArtEvents();
    const selectedArtEvent = artEventsData?.artEvents?.find(
        (event) => event.artEventID === state.selectedArtEventId
    );
    // While the Art Event list is still loading we don't yet know the waitlist state. Bias permissive in that
    // window (when an event is selected) so a valid waitlist over-request isn't wrongly blocked on first
    // render; the backend remains the real enforcement. Once loaded, use the actual value.
    const isWaitlistEnabled = selectedArtEvent
        ? selectedArtEvent.waitlistEnabled === true
        : isArtEventsLoading && !!state.selectedArtEventId;

    const [formData, setFormData] = useState<DetailedBookingSOVData>({
        entryNumber: entryId ?? 0,
        bookingId: bookingId ?? "",
        brand: initialData.brand,
        aeName: initialData.aeName,
        category: initialData.category,
        typeOfPurchase: initialData.typeOfPurchase,
        gpSlot: '',
        sovPercentageForGP: 0,
        valueBlockedByLineItem: 0,
        contextualNode: '',
        sovPercentageForContextualGP: 0,
        impressionBooked: 0,
        valueBooked: 0,
        impressionsForNonContextual: 0,
    });

    const [contextualNodes, setContextualNodes] = useState<
        SOVContextualNodeData[]
    >([
        {
            id: Date.now().toString(),
            contextualNode: '',
            sovPercentageForContextualGP: 0,
            impressionBooked: 0,
            valueBooked: 0,
        },
    ]);

    const [fieldErrors, setFieldErrors] =
        useState<DetailedBookingSOVPartialDataErrors>({
            entryNumber: '',
            gpSlot: '',
            sovPercentageForGP: '',
            valueBlockedByLineItem: '',
            contextualNode: '',
            sovPercentageForContextualGP: '',
            impressionBooked: '',
            valueBooked: '',
        });

    const [contextualNodeErrors, setContextualNodeErrors] = useState<
        SOVContextualNodeErrors[]
    >([]);

    const [hasValidationBeenTriggered, setHasValidationBeenTriggered] =
        useState(false);

    const [slotOptions, setSlotOptions] = useState<SlotOptionType[]>([]);
    const [slotPackagesData, setSlotPackagesData] = useState<SlotPackage[]>([]);


    const [activeModal, setActiveModal] = useState<
        | typeof FORM_FIELDS.VALUE_BLOCKED_BY_LINE_ITEM
        | typeof FORM_FIELDS.SOV_PERCENTAGE_FOR_GP
        | typeof FORM_FIELDS.SOV_PERCENTAGE_FOR_CONTEXTUAL_GP
        | typeof FORM_FIELDS.VALUE_BOOKED
        | null
    >(null);
    const [contextualPercentageAvailable, setContextualPercentageAvailable] = useState<number>(0);
    const [currentValidationNodeId, setCurrentValidationNodeId] = useState<string | null>(null);
    const [previousValues, setPreviousValues] = useState<PreviousSOVValues | null>(null);
    const roValue = initialData.exactBillingValue || 0;
    const effectiveRoValue = Math.max(0, roValue - budgetUsedByOtherSlots);
    const handleModalCancel = () => setActiveModal(null);

    const handleModalConfirm = () => {
        const shouldResetContextual =
            activeModal === FORM_FIELDS.SOV_PERCENTAGE_FOR_CONTEXTUAL_GP ||
            activeModal === FORM_FIELDS.VALUE_BOOKED;

        if (shouldResetContextual) {
            setContextualNodes((prev) => {
                const updatedNodes = prev.map((node) => {
                    if (node.id === currentValidationNodeId) {
                        // In edit mode, restore previous values; otherwise reset to 0
                        if (isEditMode && previousValues?.contextualNode) {
                            return {
                                ...node,
                                sovPercentageForContextualGP: previousValues.contextualNode.sovPercentageForContextualGP,
                                impressionBooked: previousValues.contextualNode.impressionBooked,
                                valueBooked: previousValues.contextualNode.valueBooked,
                            };
                        }
                        return {
                            ...node,
                            sovPercentageForContextualGP: 0,
                            impressionBooked: 0,
                            valueBooked: 0,
                        };
                    }
                    return node;
                });

                const totalValueBlocked = updatedNodes.reduce(
                    (sum, node) => sum + (node.valueBooked || 0),
                    0
                );
                setFormData((prevData) => ({
                    ...prevData,
                    valueBlockedByLineItem: totalValueBlocked,
                }));
                return updatedNodes;
            });
        } else {
            // In edit mode, restore previous values; otherwise reset to 0
            if (isEditMode && previousValues) {
                setFormData((prev) => ({
                    ...prev,
                    sovPercentageForGP: previousValues.sovPercentageForGP,
                    valueBlockedByLineItem: previousValues.valueBlockedByLineItem,
                    ...(previousValues.impressionsForNonContextual !== undefined && {
                        impressionsForNonContextual: previousValues.impressionsForNonContextual,
                    }),
                    ...(previousValues.impressionBooked !== undefined && {
                        impressionBooked: previousValues.impressionBooked,
                    }),
                }));
            } else {
                setFormData((prev) => ({
                    ...prev,
                    sovPercentageForGP: 0,
                    valueBlockedByLineItem: 0,
                }));
            }
        }
        setCurrentValidationNodeId(null);
        setPreviousValues(null);
        handleModalCancel();
    };

    const validateForm = () => {
        const { mainFormErrors, contextualNodeErrors } =
            validateDetailedBookingSOVFields({
                formData,
                contextualNodes,
                t,
                isContextualSelected
            });

        const hasMainFormErrors = Object.values(mainFormErrors).some(
            (error) => error !== ''
        );

        const hasContextualNodeErrors = contextualNodeErrors.some((nodeErrors) =>
            Object.values(nodeErrors).some((error) => error !== '')
        );

        if (hasValidationBeenTriggered) {
            setFieldErrors(mainFormErrors);
            setContextualNodeErrors(contextualNodeErrors);
        }

        return !hasMainFormErrors && !hasContextualNodeErrors;
    };

    useEffect(() => {
        if (entryId)
            setFormData((prev) => ({
                ...prev,
                entryNumber: entryId,
            }));
    }, [entryId]);

    useEffect(() => {
        if (existingContextualNodes && existingContextualNodes.length > 0) {
            const isNonContextual = existingContextualNodes[0]?.slotOrPackageName?.toLowerCase() === NON_CONTEXTUAL_PACKAGE_NAME;
            if (isNonContextual) {
                const firstSlot = existingContextualNodes[0];
                setFormData((prev) => ({
                    ...prev,
                    gpSlot: firstSlot.contextualNode || '',
                    sovPercentageForGP: firstSlot.percentageBooked || 0,
                    valueBlockedByLineItem: firstSlot.valueBlockedAtPackageLevel || 0,
                    impressionsForNonContextual: firstSlot.impressionsBooked || 0,
                }));

                setContextualNodes([
                    {
                        id: Date.now().toString(),
                        contextualNode: '',
                        sovPercentageForContextualGP: 0,
                        impressionBooked: 0,
                        valueBooked: 0,
                    }
                ]);
            } else {
                const initialContextualNodes: SOVContextualNodeData[] =
                    existingContextualNodes.map((slot, index) => ({
                        id: `${slot.contextualNode}-${index}`,
                        contextualNode: slot.contextualNode || '',
                        sovPercentageForContextualGP: slot.percentageBooked || 0,
                        impressionBooked: slot.impressionsBooked || 0,
                        valueBooked: slot.valueBlockedAtPackageLevel || 0
                    }));

                if (existingContextualNodes[0]) {
                    const firstSlot = existingContextualNodes[0];
                    setFormData((prev) => ({
                        ...prev,
                        gpSlot: firstSlot.slotOrPackageName || '',
                        sovPercentageForGP: firstSlot.percentageBooked || 0,
                        valueBlockedByLineItem: existingContextualNodes.reduce(
                            (sum, slot) => sum + (slot.valueBlockedAtPackageLevel || 0), 0
                        ),
                    }));
                }

                setContextualNodes(initialContextualNodes);
            }
        }
    }, [existingContextualNodes, formData.typeOfPurchase]);

    useEffect(() => {
        if (validateFormTrigger) {
            setHasValidationBeenTriggered(true);
            if (validateForm() && onFormSubmit)
                onFormSubmit(formData, contextualNodes);
        }
    }, [validateFormTrigger]);

    useEffect(() => {
        if (filteredSlotPackages) processSlotData({
            slotPackages: filteredSlotPackages,
            setSlotOptions,
            setSlotPackagesData,
        });
        else if (!formData.typeOfPurchase) {
            setSlotOptions([]);
            setSlotPackagesData([]);
        }
    }, [filteredSlotPackages, formData.typeOfPurchase]);

    const resetFieldErrors = () => {
        setFieldErrors({
            entryNumber: '',
            gpSlot: '',
            sovPercentageForGP: '',
            valueBlockedByLineItem: '',
            contextualNode: '',
            sovPercentageForContextualGP: '',
            impressionBooked: '',
            valueBooked: '',
        });
        setContextualNodeErrors([]);
        setHasValidationBeenTriggered(false);
    }

    useEffect(() => {
        if (resetDetailedFormErrors) {
            resetFieldErrors()
            setResetDetailedFormErrors?.(false);
        }
    }, [resetDetailedFormErrors]);

    useEffect(resetFieldErrors, []);


    const selectedOption = slotOptions.find(
        (opt) => opt.value === formData.gpSlot
    );

    const isContextualSelected = selectedOption?.type === GP_SLOT_VALUES.CONTEXTUAL;

    const isApprovedBooking = isEditMode;
    const existingNonContextualPercentage = computeExistingNonContextualPercentage(existingContextualNodes);
    const existingContextualNodePercentages = computeExistingContextualNodePercentages(existingContextualNodes);

    const confirmationModalTitle = (() => {
        if (activeModal === FORM_FIELDS.VALUE_BLOCKED_BY_LINE_ITEM || activeModal === FORM_FIELDS.VALUE_BOOKED)
            return t('value-validation-title');
        return t('percentage-validation-title');
    })();

    const confirmationModalMessage = (() => {
        if (activeModal === FORM_FIELDS.VALUE_BLOCKED_BY_LINE_ITEM || activeModal === FORM_FIELDS.VALUE_BOOKED) {
            return t('value-validation-message', {
                roValue: effectiveRoValue.toLocaleString(),
            });
        }
        if (activeModal === FORM_FIELDS.SOV_PERCENTAGE_FOR_CONTEXTUAL_GP) {
            const validatingNode = contextualNodes.find(node => node.id === currentValidationNodeId);
            const existingNodePercentage = existingContextualNodePercentages.get(validatingNode?.contextualNode || '') || 0;
            return t('percentage-validation-message', {
                percentageAvailable: getEffectiveContextualPercentageAvailable(
                    contextualPercentageAvailable,
                    existingNodePercentage
                )
            });
        }
        return t('percentage-validation-message', {
            percentageAvailable: getEffectivePercentageAvailable(
                formData.gpSlot, slotPackagesData, selectedOption,
                existingNonContextualPercentage
            )
        });
    })();

    const transformedInputChange = (field: string, value: string) => {
        // Store previous values before validation in edit mode
        if (isEditMode) {
            setPreviousValues({
                sovPercentageForGP: formData.sovPercentageForGP,
                valueBlockedByLineItem: formData.valueBlockedByLineItem,
                impressionsForNonContextual: formData.impressionsForNonContextual,
                impressionBooked: formData.impressionBooked,
            });
        }
        handleInputChange({
            setFormData,
            gpSlot: formData.gpSlot,
            slotPackagesData,
            roValue: effectiveRoValue,
            field,
            value,
            typeOfPurchase: formData.typeOfPurchase,
            setActiveModal,
            selectedOption,
            setContextualNodes,
            isApprovedBooking,
            existingPercentage: existingNonContextualPercentage,
            isEditMode,
            isWaitlistEnabled,
        });
        if (field === FORM_FIELDS.GP_SLOT) onSlotSelectionChange(value);
    };

    useEffect(() => {
        if (hasValidationBeenTriggered) {
            validateForm();
        }
    }, [formData, contextualNodes, hasValidationBeenTriggered]);

    useEffect(() => {
        onSlotBudgetChange?.(formData.valueBlockedByLineItem || 0);
    }, [formData.valueBlockedByLineItem]);

    const sovMainFormProps = {
        formData,
        handleInputChange: transformedInputChange,
        isContextualSelected,
        fieldErrors,
        hasValidationBeenTriggered,
        slotOptions,
        isLoadingSlots: false,
        roValue: effectiveRoValue,
        slotPackagesData,
        selectedOption,
        hideCommonFields,
        preSelectedSlotNames,
    };

    const partialContextualNodeFieldProps = {
        contextualNodes,
        formData,
        isContextualSelected,
        slotPackagesData,
        hasValidationBeenTriggered,
        contextualNodeErrors,
        setContextualNodes,
        setFormData,
        setActiveModal,
        roValue: effectiveRoValue,
        selectedOption,
        setContextualPercentageAvailable,
        setCurrentValidationNodeId,
        setPreviousValues,
        isEditMode,
        isApprovedBooking,
        existingContextualNodePercentages,
        isWaitlistEnabled,
    };

    const propsToReCalculatelineItemValue = {
        typeOfPurchase: formData.typeOfPurchase,
        isContextualSelected,
        setFormData,
    };

    const contextualNodesSectionProps = {
        contextualNodes,
        setContextualNodes,
        setContextualNodeErrors,
        partialContextualNodeFieldProps,
        propsToReCalculatelineItemValue,
    };

    const modalProps = {
        isOpen: !!activeModal,
        onConfirmCb: handleModalConfirm,
        onCancelCb: handleModalCancel,
        title: confirmationModalTitle,
        message: confirmationModalMessage,
        confirmButtonText: t('ok-button')
    }

    return {
        modalProps,
        sovMainFormProps,
        isContextualSelected,
        contextualNodesSectionProps,
    }
}
